/**
 * R-68 — Constraint extractor + Key Findings elevation gate.
 *
 * Read-only extraction; only `applyConstraintElevationGate` mutates when autoElevate is on.
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { interpretAqueduct4Annual } from '../aqueduct4Screening';
import {
  buildEvidenceCacheFromSed,
  type EvidenceCache,
} from './providerPrecedenceVerifier';
import { extractSections, reportFullText } from './reportTextExtract';
import {
  canonicalPhrase,
  constraintTypeLabel,
  formatConstraintValue,
  normalizeConstraintValue,
  parseQueueYears,
  severityForConstraint,
  type ConstraintSeverity,
  type ConstraintType,
} from './constraintSeverity';

export interface ExtractedConstraint {
  type: ConstraintType;
  /** Dedup key: `${type}:${normalizedValue}` */
  canonicalKey: string;
  value: string | number;
  displayValue: string;
  severity: ConstraintSeverity;
  source: 'provider' | 'regex';
  provider?: string;
  sectionId?: string;
  lineNumber?: number;
  excerpt?: string;
  /** Existing [Ref N] indices found in excerpt — never invent new ones. */
  citationRefs: number[];
  /** Source-scoped follow-up, when a regional observation cannot establish site sufficiency. */
  followUp?: string;
}

export interface ConstraintElevationFinding {
  constraint: ExtractedConstraint;
  severity: 'block' | 'warn';
  kind: 'absent_from_key_findings' | 'buried_in_exec_only' | 'buried_order';
  inKeyFindings: boolean;
  inExecutiveSummary: boolean;
  keyFindingsIndex?: number;
  suggestedBullet: string;
  message: string;
}

export interface MarkdownPatch {
  target: 'structuredFindings' | 'keyFindings' | 'markdown';
  /** Snapshot for revert — shape matches target field. */
  original: unknown;
  /** Bullets prepended (markdown strings or structured finding objects). */
  prepended: unknown[];
  appliedCount: number;
}

const REF_RE = /\[Ref\s+(\d+)\]/gi;

const CONSTRAINT_PATTERNS: Array<{
  type: ConstraintType;
  re: RegExp;
  contextRe?: RegExp;
  parse: (m: RegExpMatchArray, line: string) => { value: string | number; rawText: string } | null;
}> = [
  {
    type: 'flood_zone',
    re: /\b(?:FEMA\s+)?(?:100[- ]yr\s+)?(?:flood\s+)?zone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V|X|D|B|C)\b/i,
    contextRe: /(flood|FEMA|SFHA)/i,
    parse: (m) => ({ value: m[1].toUpperCase(), rawText: m[0] }),
  },
  {
    type: 'flood_zone',
    re: /\bzone\s+(A(?:E|H|O)?|AE|AO|AH|VE|V)\b/i,
    contextRe: /(flood|FEMA|SFHA|special flood)/i,
    parse: (m) => ({ value: m[1].toUpperCase(), rawText: m[0] }),
  },
  {
    type: 'interconnection_queue',
    re: /\b(?:interconnection|IX|LGIA|SGIA|queue)[^.\n]{0,60}(\d+(?:\.\d+)?)\s*[-–—to]+\s*(\d+(?:\.\d+)?)\s*(?:yr|year)/i,
    parse: (m) => ({
      value: `${m[1]}-${m[2]} years`,
      rawText: m[0],
    }),
  },
  {
    type: 'interconnection_queue',
    re: /\b(\d+(?:\.\d+)?)\s*\+?\s*(?:yr|year)s?\s+(?:interconnection|IX|LGIA|SGIA|queue)/i,
    parse: (m) => ({ value: parseFloat(m[1]), rawText: m[0] }),
  },
  {
    type: 'interconnection_queue',
    re: /\b(?:queue|interconnection)[^.\n]{0,40}(\d+(?:\.\d+)?)\s*(?:yr|year)s?\b/i,
    contextRe: /(queue|interconnect|LGIA|SGIA|MISO|PJM|ERCOT|CAISO|SPP)/i,
    parse: (m) => ({ value: parseFloat(m[1]), rawText: m[0] }),
  },
  {
    type: 'wetland_coverage',
    re: /\b([\d,]+(?:\.\d+)?)\s*%\s*(?:of\s+(?:the\s+)?(?:site|parcel))?\s*wetland/i,
    parse: (m) => ({ value: parseFloat(m[1].replace(/,/g, '')), rawText: m[0] }),
  },
  {
    type: 'wetland_coverage',
    re: /wetland[^.\n]{0,80}\b([\d,]+(?:\.\d+)?)\s*%/i,
    parse: (m) => ({ value: parseFloat(m[1].replace(/,/g, '')), rawText: m[0] }),
  },
  {
    type: 'water_stress',
    re: /\b(?:water\s+)?stress\s+(?:level\s+)?(?:is\s+)?(High|Moderate|Low)\b/i,
    contextRe: /(water|WRI|Aqueduct|withdrawal)/i,
    parse: (m) => ({ value: m[1], rawText: m[0] }),
  },
  {
    type: 'grid_capacity',
    re: /\bgrid\s+capacity[^.\n]{0,40}\b(Constrained|Limited|High)\b/i,
    parse: (m) => ({ value: m[1], rawText: m[0] }),
  },
];

function extractCitationRefs(text: string): number[] {
  const refs = new Set<number>();
  for (const m of text.matchAll(REF_RE)) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n)) refs.add(n);
  }
  return [...refs].sort((a, b) => a - b);
}

function formatCitationSuffix(refs: number[]): string {
  if (refs.length === 0) return '';
  return ` ${refs.map((n) => `[Ref ${n}]`).join(' ')}`;
}

function makeCanonicalKey(type: ConstraintType, value: string | number): string {
  return `${type}:${normalizeConstraintValue(type, value)}`;
}

/** The existing numeric source lane distinguishes these two explicitly scoped
 * generator-statistic phrasings from a site load timetable. Elevation must
 * preserve that role; the ordinary numeric/citation guard still verifies the
 * statistic against its source. Another assertion cannot lend this scope. */
function isGeneratorQueueBenchmark(line: string, match: RegExpMatchArray): boolean {
  const duration = /\b\d+(?:\.\d+)?\s*\+?\s*(?:yr|year)s?\b/i.exec(match[0]);
  if (!duration) return false;
  const index = (match.index ?? 0) + duration.index;
  const end = index + duration[0].length;
  const boundary = /;|\s+\|\s+|[.!?](?=\s|$)|\b(?:but|however|while|whereas|unlike|despite)\b/i;
  const prefix = line.slice(0, index).split(boundary).at(-1) ?? '';
  const clause = prefix + duration[0] + (line.slice(end).split(boundary)[0] ?? '');
  const legacy = /\bmedian\s+generator\s+queue\s+time\s+(?:is|was|of)\s*$/i.test(prefix)
    && /\bnot\s+(?:a\s+)?direct\s+analog\s+for\s+load\b/i.test(clause);
  const sourceFaithful = /\b(?:a|the)\s+median\s+of\s+(?:(?:about|approximately|roughly)\s+)?$/i.test(prefix)
    && /\b(?:generation(?:\s+and\s+storage)?\s+projects?|generators?)\b/i.test(clause)
    && /\bnot\s+(?:a\s+)?(?:(?:data[- ]cent(?:er|re)|large[- ])\s*)?load[- ]connection\s+timetable\b/i.test(clause);
  return legacy || sourceFaithful;
}

function scanLineForConstraints(
  line: string,
  lineNumber: number,
  sectionId: string | undefined,
): ExtractedConstraint[] {
  const out: ExtractedConstraint[] = [];
  for (const pat of CONSTRAINT_PATTERNS) {
    if (pat.contextRe && !pat.contextRe.test(line)) continue;
    // A qualified benchmark earlier on this line must not hide a later,
    // independently asserted site queue, so inspect each queue match.
    const matches = pat.type === 'interconnection_queue'
      ? [...line.matchAll(new RegExp(pat.re.source, `${pat.re.flags}g`))]
      : [line.match(pat.re)].filter((match): match is RegExpMatchArray => !!match);
    for (const m of matches) {
      if (pat.type === 'interconnection_queue' && isGeneratorQueueBenchmark(line, m)) continue;
      const parsed = pat.parse(m, line);
      if (!parsed) continue;
      const sev = severityForConstraint(pat.type, parsed.value);
      if (sev === 'info') continue;
      const citationRefs = extractCitationRefs(line);
      out.push({
        type: pat.type,
        canonicalKey: makeCanonicalKey(pat.type, parsed.value),
        value: parsed.value,
        displayValue: formatConstraintValue(pat.type, parsed.value),
        severity: sev,
        source: 'regex',
        sectionId,
        lineNumber,
        excerpt: line.trim().slice(0, 240),
        citationRefs,
      });
    }
  }
  return out;
}

function extractFromProviderEvidence(
  sed: SiteEvidenceDocument | null | undefined,
  evidence: EvidenceCache,
): ExtractedConstraint[] {
  const out: ExtractedConstraint[] = [];

  const flood = evidence.providerData.floodZone;
  if (flood?.value != null) {
    const sev = severityForConstraint('flood_zone', flood.value);
    if (sev !== 'info') {
      out.push({
        type: 'flood_zone',
        canonicalKey: makeCanonicalKey('flood_zone', flood.value),
        value: flood.value,
        displayValue: formatConstraintValue('flood_zone', flood.value),
        severity: sev,
        source: 'provider',
        provider: flood.tier1Source,
        excerpt: `Tier-1 FEMA flood zone ${flood.value} (${flood.provenance ?? 'sed.flood'})`,
        citationRefs: [],
      });
    }
  }

  const grid = sed?.grid?.data;
  // grid.capacity is derived from HIFLD voltage/count, never measured spare
  // capacity. Only the separate, provider-qualified headroom contract can
  // establish a known limit; preserve the exact metric and connection scope.
  const headroomSection = sed?.transmissionHeadroom;
  const headroom = headroomSection?.data;
  if (headroomSection?.status === 'verified' && headroom?.headroomBasis === 'provider'
    && headroom.provenance === 'provider' && typeof headroomSection.source === 'string' && headroomSection.source.trim()) {
    for (const [field, label] of [['atcMw', 'available transfer capacity'], ['hostingCapacityMw', 'hosting capacity']] as const) {
      if (headroom[field] !== 0) continue;
      out.push({
        type: 'grid_capacity', canonicalKey: `grid_capacity:${field}:0`, value: 'Limited',
        displayValue: `Published ${label}: 0 MW at the recorded connection point`,
        severity: 'warn', source: 'provider', provider: headroomSection.source,
        excerpt: `Published ${label}: 0 MW (sed.transmissionHeadroom.data.${field}; provider basis)`,
        followUp: 'Confirm applicability and service options with the utility; this is not a finding that the site cannot be served',
        citationRefs: [],
      });
    }
  }
  // HIFLD proximity heuristics and regional generation benchmarks do not
  // establish a load-service schedule for this site. Only elevate a duration
  // whose persisted authority explicitly identifies site load service.
  if (grid?.estimatedQueueTime && grid.estimatedQueueTimeBasis === 'site-load-service') {
    const sev = severityForConstraint('interconnection_queue', grid.estimatedQueueTime);
    if (sev !== 'info') {
      out.push({
        type: 'interconnection_queue',
        canonicalKey: makeCanonicalKey('interconnection_queue', grid.estimatedQueueTime),
        value: grid.estimatedQueueTime,
        displayValue: formatConstraintValue('interconnection_queue', grid.estimatedQueueTime),
        severity: sev,
        source: 'provider',
        provider: sed?.grid.source,
        excerpt: `Site load-service interconnection estimate ${grid.estimatedQueueTime} (sed.grid.data.estimatedQueueTime; site-load-service basis)`,
        citationRefs: [],
      });
    }
  }

  const waterSection = sed?.waterStress;
  const ws = waterSection?.data;
  if (waterSection?.status === 'verified' && ws && /WRI|Aqueduct/i.test(waterSection.source)) {
    const addWater = (score: number, label: string, concern: 'Moderate' | 'High', origin: string, scope = '') => {
      out.push({
        type: 'water_stress', canonicalKey: `water_stress:${concern}`, value: concern,
        displayValue: `Regional Aqueduct baseline: ${label} (${score}/5)${scope ? `; ${scope}` : ''}`,
        severity: severityForConstraint('water_stress', concern), source: 'provider', provider: waterSection.source,
        excerpt: `Regional baseline ${label}, ${score}/5 (${origin})`, citationRefs: [],
        followUp: 'Assess cooling demand and applicable water sources; basin stress does not establish site supply adequacy or water rights',
      });
    };
    if (ws.studyAssessment) {
      // Use individually assessed native observations, including positives in
      // partial coverage. Never turn a partial low observation into site-wide
      // clearance, or use the coarse compatibility stressLevel alias.
      for (const row of Array.isArray(ws.studyAssessment.observations) ? ws.studyAssessment.observations : []) {
        const baseline = row?.baselineStress;
        if (!baseline || baseline.status === 'unavailable' || typeof row.stringId !== 'string' || !row.stringId.trim()
          || !Number.isSafeInteger(row.numericId) || row.numericId < 0) continue;
        const interpreted = interpretAqueduct4Annual({ string_id: row.stringId, aq30_id: row.numericId,
          bws_raw: baseline.sourceRaw, bws_score: baseline.score, bws_cat: baseline.category, bws_label: baseline.label }).baselineStress;
        if (interpreted.score !== null && (interpreted.concern === 'High' || interpreted.concern === 'Moderate')) {
          addWater(interpreted.score, interpreted.label, interpreted.concern,
            `sed.waterStress.data.studyAssessment.observations; basin ${row.stringId}${row.parcelId ? `; parcel ${row.parcelId}` : ''}; ${ws.studyAssessment.status} coverage`, `known basin ${row.stringId}; study coverage ${ws.studyAssessment.status}`);
        }
      }
    } else if (typeof ws.bwsLabel === 'string') {
      // Legacy category numbering and stressLevel aliases are not stable
      // across source editions. Require the retained score and source label
      // to agree before elevating a regional baseline observation.
      const score = ws.bwsScore;
      const label = ws.bwsLabel.replace(/^\s*\d+\.\s*/, '').replace(/[–—]/g, '-').trim();
      const mediumHigh = /^Medium\s*(?:-|to)\s*High\s*\(20-40%\)$/i.test(label);
      const high = /^High\s*\(40-80%\)$/i.test(label);
      const extreme = /^Extremely\s+High\s*\(>80%\)$/i.test(label);
      const arid = /^Arid\s+(?:and|&)\s+Low\s+Water\s+Use$/i.test(label);
      if (typeof score === 'number' && Number.isFinite(score) && score >= 0 && score <= 5) {
        if ((high && score >= 3 && score <= 4) || (extreme && score >= 4) || (arid && score === 5)) addWater(score, ws.bwsLabel, 'High', 'sed.waterStress.data.bwsScore/bwsLabel');
        else if (mediumHigh && score >= 2 && score <= 3) addWater(score, ws.bwsLabel, 'Moderate', 'sed.waterStress.data.bwsScore/bwsLabel');
      }
    }
  }

  // EnvironmentalEvidence.wetlandAcres contains whole source-feature totals
  // and has no parcel-clipped area contract. Dividing it by gross site acreage
  // invents a site coverage percentage. Preserve mapped positives in the SED
  // and report; do not manufacture a quantified land deduction here. Legacy
  // values without a basis also cannot establish a measured parcel overlap.

  return out;
}

function dedupeConstraints(items: ExtractedConstraint[]): ExtractedConstraint[] {
  const byKey = new Map<string, ExtractedConstraint>();
  for (const c of items) {
    const existing = byKey.get(c.canonicalKey);
    if (!existing) {
      byKey.set(c.canonicalKey, c);
      continue;
    }
    // Prefer provider over regex; prefer block over warn; prefer excerpt with citations.
    const rank = (x: ExtractedConstraint) =>
      (x.source === 'provider' ? 2 : 1)
      + (x.severity === 'block' ? 2 : x.severity === 'warn' ? 1 : 0)
      + (x.citationRefs.length > 0 ? 1 : 0);
    if (rank(c) > rank(existing)) byKey.set(c.canonicalKey, c);
  }
  return [...byKey.values()];
}

/** Read-only — scan report prose + tier-1 provider flags for major constraints. */
export function extractConstraints(
  report: SiteRagReport,
  evidence: EvidenceCache,
  sed?: SiteEvidenceDocument | null,
): ExtractedConstraint[] {
  // This extractor ranks physical siting constraints, not observed public
  // positions. Elevating every SED hazard into a PSR turns source context into
  // negative community findings and invents a mitigation obligation. Keep
  // cited outreach context in its authored section; ordinary factual/stance
  // audits still validate that text. All audit callers use this same scope.
  if (report.reportType === 'public-sentiment') return [];
  const fromProvider = extractFromProviderEvidence(sed ?? null, evidence);
  const fromRegex: ExtractedConstraint[] = [];

  for (const section of extractSections(report)) {
    const lines = section.content.split('\n');
    for (let i = 0; i < lines.length; i++) {
      // Water/grid prose is not independent source evidence. Re-elevating it
      // would restore the very HIFLD/legacy-alias claims excluded above. Keep
      // the authored text untouched for the ordinary factual/source audits;
      // these two provider-scoped constraints come from qualified SED facts.
      fromRegex.push(...scanLineForConstraints(lines[i], i + 1, section.id)
        .filter(constraint => constraint.type !== 'water_stress' && constraint.type !== 'grid_capacity'));
    }
  }

  return dedupeConstraints([...fromProvider, ...fromRegex]);
}

function matchesConstraint(text: string, constraint: ExtractedConstraint): boolean {
  if (constraint.type === 'water_stress' || constraint.type === 'grid_capacity') {
    return text.toLowerCase().includes(constraint.displayValue.toLowerCase());
  }
  const phrase = canonicalPhrase(constraint.type, constraint.value);
  const lower = text.toLowerCase();
  if (lower.includes(phrase.toLowerCase())) return true;

  // Value-only fallback for numeric constraints
  const norm = normalizeConstraintValue(constraint.type, constraint.value);
  if (constraint.type === 'interconnection_queue') {
    const years = parseQueueYears(constraint.value);
    return lower.includes(`${years} year`) || lower.includes(`${years}-`) || lower.includes(`${years} yr`);
  }
  if (constraint.type === 'flood_zone') {
    return lower.includes(`zone ${norm.toLowerCase()}`) || lower.includes(`flood zone ${norm.toLowerCase()}`);
  }
  if (constraint.type === 'wetland_coverage') {
    return lower.includes(`${norm}%`) || lower.includes(`${norm} %`);
  }
  return lower.includes(String(norm).toLowerCase());
}

function getKeyFindingsBullets(report: SiteRagReport): string[] {
  const r = report as SiteRagReport & {
    structuredFindings?: Array<{ title?: string; detail?: string }>;
    keyFindings?: Array<string | { title?: string; detail?: string; description?: string }>;
  };
  const bullets: string[] = [];
  if (Array.isArray(r.structuredFindings)) {
    for (const f of r.structuredFindings) {
      bullets.push([f.title, f.detail].filter(Boolean).join(': '));
    }
  }
  if (Array.isArray(r.keyFindings)) {
    for (const k of r.keyFindings) {
      if (typeof k === 'string') bullets.push(k);
      else bullets.push([k.title, k.detail ?? k.description].filter(Boolean).join(': '));
    }
  }
  const md = (report as SiteRagReport & { markdown?: string }).markdown ?? '';
  const kfMatch = md.match(/##\s*Key Findings\s*\n([\s\S]*?)(?=\n##\s|\n#\s|$)/i);
  if (kfMatch) {
    for (const line of kfMatch[1].split('\n')) {
      const trimmed = line.replace(/^[-*•]\s*/, '').trim();
      if (trimmed) bullets.push(trimmed);
    }
  }
  return bullets;
}

function getKeyFindingsText(report: SiteRagReport): string {
  return getKeyFindingsBullets(report).join('\n');
}

function getExecutiveSummaryText(report: SiteRagReport): string {
  const executiveSummary = (report as { executiveSummary?: unknown }).executiveSummary;
  if (typeof executiveSummary === 'string') return executiveSummary;
  if (executiveSummary && typeof executiveSummary === 'object') {
    const summary = (executiveSummary as { summary?: unknown }).summary;
    return typeof summary === 'string' ? summary : '';
  }
  return '';
}

function buildSuggestedBullet(constraint: ExtractedConstraint): string {
  const label = constraintTypeLabel(constraint.type);
  const cite = formatCitationSuffix(constraint.citationRefs);
  const sevWord = constraint.severity === 'block' ? 'Critical' : 'Major';
  // Value-first (2026-08-03): the old template buried the fact mid-sentence
  // and repeated 40 words of boilerplate on every constraint. State the value,
  // then the obligation. Lane parsing (laneItemStructure.ts) recognises BOTH
  // forms — the old sentence persists in already-generated reports.
  // (Also supersedes the P2c semicolon rephrasing from the same-day RCA
  // branch — this form fixes the degenerate-adjective prose too.)
  return `${sevWord}: ${label} — ${constraint.displayValue} (${constraint.followUp ?? 'mitigation pathway required'})${cite}`.trim();
}

/** Check whether major constraints appear prominently in Key Findings / exec summary. */
export function detectElevationGap(
  constraints: ExtractedConstraint[],
  report: SiteRagReport,
): ConstraintElevationFinding[] {
  const findings: ConstraintElevationFinding[] = [];
  const kfBullets = getKeyFindingsBullets(report);
  const kfText = kfBullets.join('\n');
  const execText = getExecutiveSummaryText(report);

  for (const constraint of constraints) {
    if (constraint.severity === 'info') continue;

    const inKf = matchesConstraint(kfText, constraint);
    const inExec = matchesConstraint(execText, constraint);
    const suggestedBullet = buildSuggestedBullet(constraint);

    let kfIndex: number | undefined;
    for (let i = 0; i < kfBullets.length; i++) {
      if (matchesConstraint(kfBullets[i], constraint)) {
        kfIndex = i;
        break;
      }
    }

    if (!inKf && !inExec) {
      findings.push({
        constraint,
        severity: 'block',
        kind: 'absent_from_key_findings',
        inKeyFindings: false,
        inExecutiveSummary: false,
        suggestedBullet,
        message: `${constraintTypeLabel(constraint.type)} (${constraint.displayValue}) absent from Key Findings and executive summary.`,
      });
      continue;
    }

    if (inExec && !inKf) {
      findings.push({
        constraint,
        severity: 'warn',
        kind: 'buried_in_exec_only',
        inKeyFindings: false,
        inExecutiveSummary: true,
        suggestedBullet,
        message: `${constraintTypeLabel(constraint.type)} (${constraint.displayValue}) mentioned in executive summary but not elevated to Key Findings.`,
      });
      continue;
    }

    if (inKf && kfIndex != null && kfIndex >= 3) {
      findings.push({
        constraint,
        severity: 'warn',
        kind: 'buried_order',
        inKeyFindings: true,
        inExecutiveSummary: inExec,
        keyFindingsIndex: kfIndex,
        suggestedBullet,
        message: `${constraintTypeLabel(constraint.type)} (${constraint.displayValue}) buried at Key Findings position ${kfIndex + 1}.`,
      });
    }
  }

  return findings;
}

/** Severity words that must never be used AS a finding title (they are a label, not a title). */
const SEVERITY_TITLE_WORDS = new Set(['critical', 'major', 'warning', 'block', 'minor', 'info']);

/**
 * P2c (RCA 2026-08-03) — `source` carries the constraint's provenance.
 * The old hardcoded `source: ''` made every auto-elevated finding fail the
 * decision-analysis bridge's provenance filter (`fromReport.ts` drops
 * findings with an empty source), so a report could show six key findings
 * while its analysis snapshot showed zero.
 */
function constraintSourceLabel(constraint?: Pick<ExtractedConstraint, 'provider' | 'sectionId'>): string {
  return constraint?.provider?.trim()
    || constraint?.sectionId?.trim()
    || 'constraint-extractor';
}

function structuredFindingFromBullet(
  bullet: string,
  constraint?: Pick<ExtractedConstraint, 'provider' | 'sectionId'>,
): { title: string; detail: string; source: string; impact: string } {
  const source = constraintSourceLabel(constraint);
  const colon = bullet.indexOf(':');
  if (colon > 0) {
    const head = bullet.slice(0, colon).trim();
    const rest = bullet.slice(colon + 1).trim();
    // Bullets are built as "Critical: <Label> — <detail>". A bare severity word
    // ("Critical"/"Major") is a placeholder, not a title — promote the real
    // constraint label (the segment before the em/en/hyphen dash) to the title.
    if (SEVERITY_TITLE_WORDS.has(head.toLowerCase())) {
      const dash = rest.match(/\s[—–-]\s/);
      if (dash && dash.index != null) {
        const title = rest.slice(0, dash.index).trim();
        const detail = rest.slice(dash.index + dash[0].length).trim();
        if (title) {
          return { title, detail: detail || rest, source, impact: 'negative' };
        }
      }
      // No dash to split on — fall back to the whole remainder as the title.
      return { title: rest || head, detail: '', source, impact: 'negative' };
    }
    return {
      title: head,
      detail: rest,
      source,
      impact: 'negative',
    };
  }
  return { title: bullet, detail: '', source, impact: 'negative' };
}

/** Build reversible patch prepending elevation bullets to Key Findings. */
export function buildElevationPatch(
  findings: ConstraintElevationFinding[],
  report: SiteRagReport,
): MarkdownPatch | null {
  const toElevate = findings.filter((f) => f.severity === 'block' || f.severity === 'warn');
  if (toElevate.length === 0) return null;

  const bullets = toElevate.map((f) => f.suggestedBullet);
  const r = report as SiteRagReport & {
    structuredFindings?: Array<{ title: string; detail: string; source: string; impact: string }>;
    keyFindings?: Array<string | { title?: string; detail?: string }>;
    markdown?: string;
  };

  if (Array.isArray(r.structuredFindings)) {
    const structured = toElevate.map((f) => structuredFindingFromBullet(f.suggestedBullet, f.constraint));
    return {
      target: 'structuredFindings',
      original: [...r.structuredFindings],
      prepended: structured,
      appliedCount: structured.length,
    };
  }

  if (Array.isArray(r.keyFindings)) {
    return {
      target: 'keyFindings',
      original: [...r.keyFindings],
      prepended: bullets,
      appliedCount: bullets.length,
    };
  }

  const md = r.markdown ?? '';
  const kfHeader = md.match(/##\s*Key Findings/i);
  if (kfHeader) {
    return {
      target: 'markdown',
      original: md,
      prepended: bullets.map((b) => `- ${b}`),
      appliedCount: bullets.length,
    };
  }

  // Default: create structuredFindings
  const structured = toElevate.map((f) => structuredFindingFromBullet(f.suggestedBullet, f.constraint));
  return {
    target: 'structuredFindings',
    original: r.structuredFindings ?? null,
    prepended: structured,
    appliedCount: structured.length,
  };
}

/** Apply a prepared elevation patch (admin promote or auto-elevate). */
export function applyElevationPatch(report: SiteRagReport, patch: MarkdownPatch): SiteRagReport {
  let out = applyPatchToReport(report, patch);
  out = appendElevationFootnote(out, patch.appliedCount);
  return {
    ...out,
    qaFindings: {
      ...(out.qaFindings ?? {}),
      elevationPatch: {
        original: patch.original,
        appliedAt: new Date().toISOString(),
        appliedCount: patch.appliedCount,
        target: patch.target,
      },
    },
  };
}

/** Admin promote — apply Key Findings elevation from stored findings. */
export function promoteConstraintsToKeyFindings(report: SiteRagReport): SiteRagReport {
  const findings = (report.qaFindings?.constraintElevationFindings ?? []).map((f) => ({
    constraint: {
      type: f.type as ConstraintType,
      canonicalKey: f.canonicalKey,
      value: f.value,
      displayValue: f.displayValue,
      severity: f.constraintSeverity,
      source: f.source ?? 'regex',
      provider: f.provider,
      sectionId: f.sectionId,
      lineNumber: f.lineNumber,
      excerpt: f.excerpt,
      citationRefs: f.citationRefs ?? [],
    } as ExtractedConstraint,
    severity: f.severity,
    kind: f.kind,
    inKeyFindings: f.inKeyFindings,
    inExecutiveSummary: f.inExecutiveSummary,
    keyFindingsIndex: f.keyFindingsIndex,
    suggestedBullet: f.suggestedBullet,
    message: f.message,
  }));
  const patch = buildElevationPatch(findings, report);
  if (!patch) return report;
  return applyElevationPatch(report, patch);
}

function applyPatchToReport(report: SiteRagReport, patch: MarkdownPatch): SiteRagReport {
  const out = { ...report } as SiteRagReport & {
    structuredFindings?: Array<{ title: string; detail: string; source: string; impact: string }>;
    keyFindings?: unknown[];
    markdown?: string;
  };

  if (patch.target === 'structuredFindings') {
    const existing = Array.isArray(out.structuredFindings) ? out.structuredFindings : [];
    out.structuredFindings = [
      ...(patch.prepended as typeof existing),
      ...existing,
    ];
    return out;
  }

  if (patch.target === 'keyFindings') {
    const existing = Array.isArray(out.keyFindings) ? out.keyFindings : [];
    out.keyFindings = [...(patch.prepended as typeof existing), ...existing];
    return out;
  }

  if (patch.target === 'markdown' && typeof out.markdown === 'string') {
    const md = out.markdown;
    const match = md.match(/(##\s*Key Findings\s*\n)/i);
    if (match && match.index != null) {
      const insertAt = match.index + match[0].length;
      const prependBlock = (patch.prepended as string[]).join('\n') + '\n';
      out.markdown = md.slice(0, insertAt) + prependBlock + md.slice(insertAt);
    }
    return out;
  }

  return out;
}

const ELEVATION_FOOTNOTE = '(N constraints auto-elevated to Key Findings — see Audit tab)';

function appendElevationFootnote(report: SiteRagReport, count: number): SiteRagReport {
  const footnote = ELEVATION_FOOTNOTE.replace('N', String(count));
  const out = { ...report } as SiteRagReport & { markdown?: string };
  const md = out.markdown;
  if (typeof md === 'string' && !md.includes('auto-elevated to Key Findings')) {
    out.markdown = `${md}\n\n_${footnote}_`;
  }
  return out;
}

/** Run extractor + gap detection; optionally auto-elevate constraints to Key Findings. */
export function applyConstraintElevationGate(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  opts: { autoElevate?: boolean } = {},
): SiteRagReport {
  const evidence = buildEvidenceCacheFromSed(sed);
  const constraints = extractConstraints(report, evidence, sed);
  const elevationFindings = detectElevationGap(constraints, report);

  if (constraints.length === 0 && elevationFindings.length === 0) return report;

  let out: SiteRagReport = {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      constraintElevationFindings: elevationFindings.map((f) => ({
        type: f.constraint.type,
        canonicalKey: f.constraint.canonicalKey,
        value: f.constraint.value,
        displayValue: f.constraint.displayValue,
        constraintSeverity: f.constraint.severity,
        severity: f.severity,
        kind: f.kind,
        inKeyFindings: f.inKeyFindings,
        inExecutiveSummary: f.inExecutiveSummary,
        keyFindingsIndex: f.keyFindingsIndex,
        excerpt: f.constraint.excerpt,
        sectionId: f.constraint.sectionId,
        lineNumber: f.constraint.lineNumber,
        source: f.constraint.source,
        provider: f.constraint.provider,
        citationRefs: f.constraint.citationRefs,
        suggestedBullet: f.suggestedBullet,
        message: f.message,
      })),
    },
  };

  const patch = buildElevationPatch(elevationFindings, report);
  if (opts.autoElevate && patch && patch.appliedCount > 0) {
    out = applyElevationPatch(out, patch);
    /*
     * ELEVATED ⇒ RESOLVED (2026-08-04, live v1.22.0 Redhead run): the stamped
     * findings describe the PRE-patch gap ("constraint absent from Key
     * Findings"), and the patch just FIXED that gap in this same pass — yet
     * block-severity entries in qaFindings feed containsBlockSeverity ⇒
     * hardGatesPassed=false ⇒ the analysis fails its hard gate over a defect
     * that no longer exists (observed live: appliedCount=4 with 4 block
     * findings still standing). Downgrade the applied findings to 'warn' and
     * mark them auto-elevated so the record keeps full observability without
     * failing the gate for a repaired condition. The un-applied branch below
     * keeps its block severity — there the gap genuinely persists.
     */
    out = {
      ...out,
      qaFindings: {
        ...(out.qaFindings ?? {}),
        constraintElevationFindings: (out.qaFindings?.constraintElevationFindings ?? []).map(
          (finding) => ({
            ...finding,
            severity: 'warn' as const,
            autoElevated: true,
          }),
        ),
      },
    };
  } else if (patch) {
    out = {
      ...out,
      qaFindings: {
        ...(out.qaFindings ?? {}),
        elevationPatch: {
          original: patch.original,
          appliedCount: 0,
          target: patch.target,
        },
      },
    };
  }

  if (elevationFindings.length > 0) {
    console.info('[R-68 constraint elevation]', {
      reportId: out.id,
      constraints: constraints.length,
      blocks: elevationFindings.filter((f) => f.severity === 'block').length,
      warns: elevationFindings.filter((f) => f.severity === 'warn').length,
      autoElevated: opts.autoElevate && (patch?.appliedCount ?? 0) > 0,
    });
  }

  return out;
}

/** Revert a prior auto-elevation using stored elevationPatch.original. */
export function revertConstraintElevation(report: SiteRagReport): SiteRagReport {
  const patch = report.qaFindings?.elevationPatch;
  if (!patch?.original) return report;

  const out = { ...report } as SiteRagReport & {
    structuredFindings?: unknown;
    keyFindings?: unknown;
    markdown?: string;
  };

  const target = patch.target ?? 'structuredFindings';
  if (target === 'structuredFindings') {
    if (patch.original === null) delete out.structuredFindings;
    else out.structuredFindings = patch.original as SiteRagReport['structuredFindings'];
  } else if (target === 'keyFindings') {
    out.keyFindings = patch.original as Array<string | { title?: string; detail?: string }>;
  } else if (target === 'markdown') {
    out.markdown = patch.original as string;
  }

  const { elevationPatch: _removed, ...restQa } = out.qaFindings ?? {};
  out.qaFindings = {
    ...restQa,
    constraintElevationFindings: out.qaFindings?.constraintElevationFindings,
  };

  return out;
}

/** Full report text helper for CLI / audit scripts. */
export { reportFullText };
